import type { StepGroup as RawSwimlane } from "../../../types/rawProcess.types";
import { dbOps } from "../hooks/dbOps.store";
import { swimlaneModelCreator } from "../../../models/swimlane.model";
import { insertItemAtIndex, reorderItems } from "@certa/common/utils/array";
import { createSwimlane, type Swimlane } from "@certa/studio-core";
import { importRulesToStudio } from "./rule.actions";
import { Intents } from "@certa/studio-core/src/constants/activityLogs.constants";

// TODO: Need to check tags. For ALL utils
export const importRawSwimlanes = ({
  rawSwimlanes,
  processId,
  intent,
  templateId
}: {
  rawSwimlanes: RawSwimlane[];
  processId: string;
  intent: Intents;
  templateId?: string;
}) => {
  dbOps().transaction(
    () => {
      // Important to get process here and modify the reference
      // As calling it again in the loop, will return the same value
      // as it will not be updated till the end of the transaction
      const process = dbOps().getProcess(processId);

      for (const rawSwimlane of rawSwimlanes) {
        const {
          swimlane,
          conditionGroupMap,
          conditionMap,
          fieldsMap,
          ruleMap,
          stepsMap
        } = swimlaneModelCreator(rawSwimlane, { processId });

        process?.swimlaneSequence.push(swimlane.id);
        dbOps().setSwimlane(processId, swimlane);
        dbOps().bulkSetSteps(processId, Object.values(stepsMap));
        dbOps().bulkSetFields(processId, Object.values(fieldsMap));
        importRulesToStudio({
          processId,
          ruleMap,
          conditionMap,
          conditionGroupMap
        });
      }

      dbOps().updateProcess(processId, {
        swimlaneSequence: process?.swimlaneSequence
      });
    },
    { intent, templateId }
  );
};

export const importModuleTemplateSwimlanes = (props: {
  rawSwimlanes: RawSwimlane[];
  processId: string;
  templateId: string;
}) =>
  importRawSwimlanes({
    ...props,
    intent: Intents.IMPORT_MODULE_TEMPLATE
  });

export const importTemplateRawSwimlane = ({
  rawSwimlane,
  processId,
  templateId
}: {
  rawSwimlane: RawSwimlane;
  processId: string;
  templateId: string;
}) =>
  importRawSwimlanes({
    rawSwimlanes: [rawSwimlane],
    processId,
    intent: Intents.IMPORT_SWIMLANE_TEMPLATE,
    templateId
  });

export const addSwimlane = (props: {
  processId: string;
  swimlaneData?: Partial<Swimlane>;
  index?: number;
}): string | undefined => {
  const { processId, swimlaneData, index } = props;

  const process = dbOps().getProcess(processId);
  if (!process) return;

  return dbOps().transaction(
    () => {
      const newSwimlane = createSwimlane({ processId, props: swimlaneData });
      dbOps().addSwimlane(processId, newSwimlane);

      const newSwimlaneSequence = insertItemAtIndex({
        array: process.swimlaneSequence,
        item: newSwimlane.id,
        index
      });
      dbOps().updateProcess(processId, {
        swimlaneSequence: newSwimlaneSequence
      });

      return newSwimlane.id;
    },
    { intent: Intents.ADD_SWIMLANE }
  );
};

export const deleteSwimlane = ({
  processId,
  swimlaneId
}: {
  processId: string;
  swimlaneId: string;
}) => {
  dbOps().transaction(
    () => {
      const process = dbOps().getProcess(processId);
      if (!process) return;

      const newSwimlaneSequence = process.swimlaneSequence.filter(
        id => id !== swimlaneId
      );

      dbOps().updateProcess(processId, {
        swimlaneSequence: newSwimlaneSequence
      });
      dbOps().removeSwimlane(processId, swimlaneId); // This cascade deletes steps, fields, rules, conditions too
    },
    { intent: Intents.DELETE_SWIMLANE }
  );
};

export const updateSwimlane = ({
  processId,
  swimlaneId,
  swimlaneProps,
  shouldSkipUpdatingModifiedAt
}: {
  processId: string;
  swimlaneId: string;
  swimlaneProps: Partial<Swimlane>;
  shouldSkipUpdatingModifiedAt?: boolean;
}) =>
  dbOps().transaction(
    () =>
      dbOps().updateSwimlane(
        processId,
        swimlaneId,
        swimlaneProps,
        shouldSkipUpdatingModifiedAt
      ),
    { intent: Intents.UPDATE_SWIMLANE }
  );

export const moveSwimlane = (props: {
  processId: string;
  swimlaneId: string;
  targetIndex: number;
}) => {
  const { processId, swimlaneId, targetIndex } = props;

  dbOps().transaction(
    () => {
      const process = dbOps().getProcess(processId);
      if (!process) return;

      const newSwimlaneSequence = reorderItems({
        array: process.swimlaneSequence,
        item: swimlaneId,
        targetIndex
      });

      dbOps().updateProcess(processId, {
        swimlaneSequence: newSwimlaneSequence
      });
    },
    { intent: Intents.REORDER_SWIMLANE }
  );
};
